import { withWindow } from '@shared/utils/withWindow';
import { config } from '@static/js/joy';
import { readGuestLanguageCookie } from '@shared/core/i18n/guestLanguage';

export const performFullPageRedirect = (path: string, shouldReplace: boolean = false) => {
  withWindow(global => {
    const url = global.location.origin + path;
    if (shouldReplace) {
      global.location.replace(url);
    } else {
      global.location.href = url;
    }
  });
};

/**
 * Language query aliases charm RSVP already honors (see LANGUAGE_PARAMS in
 * useGeoLocale — inlined so this util does not pull the geo-locale hook graph).
 * `'lng'` is intentionally omitted: it collides with hotel search longitude.
 */
const RSVP_LANGUAGE_PARAMS = ['language', 'lang', 'l'] as const;

/**
 * Build a guest RSVP path with a `v=1` cache-bust query param.
 *
 * The RSVP deadline middleware may send a 302 redirect when the deadline is
 * closed. Without cache-busting, browsers can replay that cached redirect
 * even after the host extends the deadline. Appending `v=1` ensures the
 * browser treats the navigation as a fresh URL that bypasses any stale 302.
 *
 * When the guest-site language picker has a saved pick and the URL does not
 * already carry a language param, stamp `language=` so the charm RSVP app
 * (which historically ignored the `joyGuestLanguage` cookie) renders in the
 * same language as the guest site.
 */
export const buildRsvpPath = (eventHandle: string | undefined, existingSearch = ''): string => {
  const base = `/${eventHandle || ''}/rsvp`;
  const params = new URLSearchParams(existingSearch);
  params.set('v', '1');
  const hasLanguageParam = RSVP_LANGUAGE_PARAMS.some(key => params.has(key));
  if (!hasLanguageParam) {
    const cookieLanguage = readGuestLanguageCookie();
    if (cookieLanguage) {
      params.set('language', cookieLanguage);
    }
  }
  return `${base}?${params.toString()}`;
};

// Encode `prev` via searchParams to avoid a double-`?` when the path already contains a query string.
export const buildAuthUrl = (route: '/login' | '/signup', returnPath: string): string => {
  const url = new URL(route, config.clientUri);
  url.searchParams.set('prev', returnPath);
  return url.href;
};

export const buildReturnPath = (loc: { pathname: string; search: string; hash: string }): string => `${loc.pathname}${loc.search}${loc.hash}`;
